import React, { useEffect, useState } from 'react';
import { toast } from '../../../../ui/toast';
import { Button } from '../../../../ui';
import Pagination from '../../../../ui/components/Common/DataTable/Paginaion';
import { CustomSelect } from '../../../../ui/custom_react_select/custom_select';
import { AddIcon } from '../../../../ui/icons/icons';
import { TextInput } from '../../../../ui/input/text_input/text_input';
import TableSkeletonLoader from '../../../../ui/TableSkeletonLoader';
import {
  createPreScreenQuestion,
  searchPreScreenQuestions,
} from '../../../../utils/api_service/pre_screen_api';
import {
  PreScreenQuestionBank,
  PreScreenQuestionType,
} from '../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../utils/types/react_select_types';
import { PreScreenQuestion } from '../PreScreenQuestion/index';

export const questionTypeOptions: SelectOptions[] = [
  {
    label: 'Numeric',
    value: 'numeric',
  },
  {
    label: 'Multiple Choice',
    value: 'multiple-choice',
  },
  {
    label: 'Video',
    value: 'video',
  },
];

export const PreScreenBankSearch = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [questionType, setQuestionType] =
    useState<PreScreenQuestionType | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalQuestions, setTotalQuestions] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [questionsList, setQuestionsList] = useState<PreScreenQuestionBank[]>(
    [],
  );
  const [newQuestion, setNewQuestion] = useState<boolean>(false);
  const [newQuestionType, setNewQuestionType] =
    useState<PreScreenQuestionType>('numeric');
  const [newQuestionText, setNewQuestionText] = useState<string>('');
  const [newQuestionDefault, setNewQuestionDefault] = useState<boolean>(false);
  const [choices, setChoices] = useState<string[]>(['']);
  const rowsPerPage = 10;

  useEffect(() => {
    setPageNumber(1);
  }, [searchTerm, questionType]);

  useEffect(() => {
    const debounceSearchTerm = setTimeout(async () => {
      setLoading(true);

      try {
        const response = await searchPreScreenQuestions(
          searchTerm,
          questionType as PreScreenQuestionType,
          pageNumber,
          rowsPerPage,
        );
        if (!response.ok) {
          throw new Error();
        }
        const result = await response.json();
        setQuestionsList(result.questions);
        setPageNumber(result.page);
        setTotalPages(Math.ceil(result.total / rowsPerPage));
        setTotalQuestions(result.total);
      } catch (error) {
        toast.error('Error fetching questions', { position: 'bottom-center' });
      } finally {
        setLoading(false);
      }
    }, 1000);

    return () => clearTimeout(debounceSearchTerm);
  }, [searchTerm, questionType, pageNumber]);

  const handleChoiceChange = (index: number, value: string) => {
    const newChoices = [...choices];
    newChoices[index] = value;
    setChoices(newChoices);
  };

  const validateQuestionData = (
    questionText: string,
    questionType: PreScreenQuestionType,
    choices: string[],
    defaultQuestion: boolean,
  ) => {
    const trimmedQuestionText = questionText.trim();
    const filteredChoices = choices.filter((choice) => choice.trim() !== '');

    if (!trimmedQuestionText) {
      toast.error('Question text cannot be empty', {
        position: 'bottom-center',
      });
      return null;
    }

    if (questionType === 'multiple-choice' && filteredChoices.length === 0) {
      toast.error('Choices cannot be empty for a multiple-choice question', {
        position: 'bottom-center',
      });
      return null;
    }

    const Question: PreScreenQuestionBank = {
      question: trimmedQuestionText,
      questionType: questionType,
      choices: questionType === 'multiple-choice' ? filteredChoices : [],
      defaultQuestion: defaultQuestion,
    };

    return Question;
  };

  const handleAddQuestion = async () => {
    const newQuestionData: PreScreenQuestionBank | null = validateQuestionData(
      newQuestionText,
      newQuestionType,
      choices,
      newQuestionDefault,
    );

    if (!newQuestionData) return;

    try {
      const response = await createPreScreenQuestion(newQuestionData);
      if (!response.ok) {
        throw new Error();
      }

      const result = await response.json();

      setQuestionsList((prevQuestions) => [result, ...prevQuestions]);

      toast.success('Question Added Successfully', {
        position: 'bottom-center',
      });

      setNewQuestionText('');
      setChoices(['']);
      setNewQuestion(false);
    } catch (error) {
      toast.error('Error adding question', { position: 'bottom-center' });
    }
  };

  const handleDeleteQuestion = (questionId: string) => {
    setQuestionsList(
      questionsList.filter((question) => question.id !== questionId),
    );
  };

  const handleEditQuestion = (editQuestion: PreScreenQuestionBank) => {
    setQuestionsList(
      questionsList.map((question) =>
        question.id === editQuestion.id ? editQuestion : question,
      ),
    );
  };

  return (
    <div className='flex w-full flex-col gap-3'>
      <div className='rounded-lg border border-emerald-100 bg-emerald-50/60 px-3 py-2 text-center text-xs font-medium text-slate-600'>
        You can view all the pre-screen questions that are used in all assessments.
      </div>

      <div className='rounded-lg border border-slate-200 bg-white p-2 shadow-sm'>
        <div className='flex w-full flex-col gap-2 lg:flex-row lg:items-center'>
          <div className='min-w-0 flex-1'>
            <TextInput
                name='searchQuestions'
                value={searchTerm}
                type='text'
                placeholder='Search questions'
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
            />
          </div>
          <div className='w-full lg:w-72'>
            <CustomSelect
                name='questionType'
                placeholder='Select Question Type'
                value={questionTypeOptions.find(
                  (option) => option.value === questionType,
                )}
                options={questionTypeOptions}
                onChange={(selectedOption: SelectOptions | null) =>
                  setQuestionType(
                    selectedOption
                      ? (selectedOption.value as 'numeric' | 'multiple-choice')
                      : null,
                  )
                }
                clearable
            />
          </div>
          <div className='w-full lg:w-auto'>
            <Button
                onClick={() => setNewQuestion(!newQuestion)}
                variant='secondary'
                label='New Question'
                hasIcon
                icon={<AddIcon />}
                className='w-full md:w-auto !border-slate-300 !text-slate-700'
            />
          </div>
        </div>
      </div>
      { newQuestion && (
        <div className='mt-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm'>
          <div className='mb-2 flex items-center justify-between'>
            <p className='text-sm font-semibold text-slate-800'>
              Create Pre-screen Question
            </p>
            <span className='rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700'>
              Draft
            </span>
          </div>

          <div className='grid gap-2 md:grid-cols-3'>
            <div className='md:col-span-2'>
              <TextInput
                  label='Question'
                  name='question'
                  value={newQuestionText}
                  type='text'
                  onChange={(e) => setNewQuestionText(e.target.value)}
              />
            </div>
            <div className='md:col-span-1'>
              <CustomSelect
                  label='Question Type'
                  name='questionType'
                  value={questionTypeOptions.find(
                    (option) => option.value === newQuestionType,
                  )}
                  options={questionTypeOptions}
                  onChange={(selectedOption: SelectOptions) =>
                    setNewQuestionType(
                    selectedOption.value as PreScreenQuestionType,
                    )
                  }
              />
            </div>
          </div>

          { newQuestionType === 'multiple-choice' && (
            <div className='mt-2 space-y-1.5'>
              <p className='text-xs font-semibold uppercase tracking-wide text-slate-500'>
                Choices
              </p>
              { choices.map((choice, index) => (
                <div key={index} className='flex items-center gap-2'>
                  <div className='w-full'>
                    <TextInput
                        name='choice'
                        value={choice}
                        type='text'
                        onChange={(e) =>
                          handleChoiceChange(index, e.target.value)
                        }
                    />
                  </div>
                  <div className='shrink-0'>
                    { index === choices.length - 1 && (
                      <Button
                          onClick={() => setChoices([...choices, ''])}
                          variant='secondary'
                          label='Add Choice'
                          className='!border-slate-300 !text-slate-700'
                      />
                    ) }
                  </div>
                </div>
              )) }
            </div>
          ) }

          <div className='mt-2 flex w-full items-center justify-between border-t border-slate-100 pt-2'>
            <label className='flex cursor-pointer items-center gap-2'>
              <input
                  type='checkbox'
                  className='h-4 w-4 accent-emerald-600'
                  onChange={() => {
                    setNewQuestionDefault(!newQuestionDefault);
                  }}
                  checked={newQuestionDefault}
              />
              <span className='text-xs font-medium text-slate-600'>
                Set as default
              </span>
            </label>
            <div className='shrink-0'>
              <Button
                  onClick={() => handleAddQuestion()}
                  variant='primary'
                  label='Add Question'
              />
            </div>
          </div>
        </div>
      ) }
      <div className='flex-grow'>
        <div className='flex flex-col rounded-lg border border-slate-200 bg-white shadow-sm'>
          { loading ? (
            <div className='p-2'>
              <TableSkeletonLoader numRows={9} height={12} />
            </div>
          ) : questionsList.length === 0 ? (
            <div className='flex min-h-[220px] flex-col items-center justify-center px-4 py-8 text-center'>
              <div className='mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400'>
                <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='h-6 w-6'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01'
                  />
                </svg>
              </div>
              <p className='text-sm font-bold text-slate-800'>
                No pre-screen questions found
              </p>
              <p className='mt-1 max-w-md text-xs text-slate-500'>
                Try adjusting your search or create a new pre-screen question.
              </p>
            </div>
          ) : (
            <div className='p-1.5'>
              { questionsList.map((question, indexNum) => (
                <PreScreenQuestion
                    question={question}
                    deleteQuestion={handleDeleteQuestion}
                    editQuestion={handleEditQuestion}
                    key={indexNum}
                    validateQuestion={validateQuestionData}
                />
              )) }
            </div>
          ) }
        </div>
      </div>
      { !loading && questionsList.length > 0 && (
        <div className='rounded-lg border border-slate-200 bg-white shadow-sm'>
          <Pagination
              currentPage={pageNumber}
              totalPages={totalPages}
              totalRecords={totalQuestions}
              rowsPerPage={rowsPerPage}
              handlePageChange={setPageNumber}
          />
        </div>
      ) }
    </div>
  );
};
